import { useState, useEffect, useCallback } from "react";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";
import { useSelector, useDispatch } from "react-redux";
import { tourSteps, tourStyles } from "../../config/tourSteps";
import { TourActions } from "../api/RequestUtil";
import {
  setTourProgress,
  updateTourStep as updateTourStepAction,
  completeTour as completeTourAction,
} from "../../redux/features/userSlice";

const Tour = () => {
  const dispatch = useDispatch();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Get tour state from Redux
  const tourProgress = useSelector((state) => state.user?.tourProgress);
  const userId = useSelector((state) => state.user?.id);

  const fetchTourProgress = useCallback(async () => {
    try {
      const response = await TourActions.getTourProgress();
      if (response.data?.status === "success") {
        // Dispatch to Redux to store tour progress
        dispatch(setTourProgress(response.data.data));
      }
    } catch (error) {
      console.error("Error fetching tour progress:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    // Fetch tour progress when component mounts
    if (userId && !tourProgress) {
      fetchTourProgress();
    }
  }, [userId, tourProgress, fetchTourProgress]);

  useEffect(() => {
    // Start tour if user hasn't completed it
    if (tourProgress && !tourProgress.tour_completed) {
      setStepIndex(tourProgress.last_step_viewed || 0);
      // Delay tour start to ensure DOM elements are ready
      setTimeout(() => {
        setRun(true);
      }, 1000);
    }
  }, [tourProgress]);

  const handleJoyrideCallback = async (data) => {
    const { status, type, index, action } = data;

    // Update step progress
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);

      // Update backend with current step
      try {
        await TourActions.updateTourStep(index);

        // Update local state
        dispatch(updateTourStepAction(index));
      } catch (error) {
        console.error("Error updating tour step:", error);
      }

      setStepIndex(nextStepIndex);
    }

    // Handle tour completion or skip
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);

      try {
        await TourActions.completeTour();

        // Update Redux state
        dispatch(completeTourAction());
      } catch (error) {
        console.error("Error completing tour:", error);
      }
    }
  };

  // Don't render if tour is already completed or user not authenticated
  if (!userId || (tourProgress && tourProgress.tour_completed)) {
    return null;
  }

  return (
    <Joyride
      steps={tourSteps}
      run={run}
      stepIndex={stepIndex}
      continuous
      showProgress
      showSkipButton
      disableOverlayClose
      disableCloseOnEsc
      spotlightClicks
      callback={handleJoyrideCallback}
      styles={tourStyles}
      locale={{
        back: "Back",
        close: "Close",
        last: "Finish",
        next: "Next",
        skip: "Skip tour",
      }}
    />
  );
};

export default Tour;
